import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../util/api";
import {
    Box, Paper, Typography, Chip, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Divider, Button, Stack, CircularProgress, Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ActionButton from "../../../component/common/ActionButton";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { notify } from "../../../util/toast";
import { runApiAction } from "../../../util/apiFeedback";

import { API } from "../../auth/admin/api/endpoints";
import { ds } from "../../../styles/designSystem";
import { makeCodeStyleResolver } from "../../../util/permissionGroups";
import DepartmentDeleteModal    from "../../auth/admin/modals/DepartmentDeleteModal";
import DepartmentPermissionModal from "../../auth/admin/modals/DepartmentPermissionModal";
import LoadingDots from "../../../component/common/LoadingDots";
import ResizableTable from "../../../component/common/ResizableTable";
import FormModal from "../../../component/common/FormModal";
import FormField from "../../../component/common/FormField";

function PermChips({ codes, codeToLabel, codeToStyle, onMore }) {
    const list = Array.isArray(codes) ? codes : [];
    const head = list.slice(0, 4);
    const extra = list.length - head.length;
    if (list.length === 0) return <Typography sx={ds.typography.caption}>없음</Typography>;
    return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {head.map(c => {
                const s = codeToStyle(c);
                return (
                    <Chip key={c} label={codeToLabel(c)} size="small"
                        sx={{ bgcolor: s.bg, border: `1px solid ${s.border}`, color: s.color, fontWeight: 600, fontSize: 11 }} />
                );
            })}
            {extra > 0 && (
                <ActionButton variant="secondary"
                    startIcon={false}
                    onClick={(e) => { e.stopPropagation(); onMore?.(); }}
                    sx={{ fontSize: 11, fontWeight: 700, p: 0, minWidth: 0,
                        borderColor: "transparent",
                        "&:hover": { borderColor: "transparent", bgcolor: "transparent", color: ds.colors.primary } }}
                    label={`+${extra}`} />
            )}
        </Stack>
    );
}

export default function HrDepartmentsTab() {
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [permissionDb, setPermissionDb] = useState([]);
    const [deptPermMap, setDeptPermMap] = useState({});

    const [newName, setNewName] = useState("");
    const [editTarget, setEditTarget] = useState(null);
    const [editName, setEditName] = useState("");
    const [openDelete, setOpenDelete] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [openPerm, setOpenPerm] = useState(false);
    const [permDeptId, setPermDeptId] = useState(null);

    const norm = (v) => String(v ?? "").trim().toUpperCase();

    const codeToLabel = useMemo(() => {
        const map = {};
        for (const p of permissionDb) {
            const code = norm(p.codeEnum ?? p.code);
            const label = String(p.permissionNameKr ?? "").trim();
            if (code) map[code] = label || code;
        }
        return (code) => map[norm(code)] || norm(code);
    }, [permissionDb]);

    // 칩 색상은 권한 목록의 domain 필드로 정한다 — 코드 목록을 프론트에 다시 두지 않는다.
    const codeToStyle = useMemo(() => makeCodeStyleResolver(permissionDb), [permissionDb]);

    const deptMap = useMemo(() => {
        const m = {};
        for (const d of departments) m[d.deptId] = d.deptName;
        return m;
    }, [departments]);

    const [sort, setSort] = useState({ key: "deptName", dir: "asc" });
    const toggleSort = (key) => setSort(s => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
    const sortLabel = (key, label) => `${label}${sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ⇅"}`;
    const sortedDepts = useMemo(() => {
        return [...departments].sort((a, b) => {
            const av = a[sort.key] ?? ""; const bv = b[sort.key] ?? "";
            const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv), "ko");
            return sort.dir === "asc" ? cmp : -cmp;
        });
    }, [departments, sort]);

    const load = async () => {
        setLoading(true);
        try {
            const [deptData, permData, deptPermData] = await Promise.all([
                api.get(API.departments.list).catch(() => []),
                api.get(API.permissions.listDb).catch(() => []),
                api.get(API.adminPermissions.allDepts).catch(() => ({})),
            ]);
            setDepartments(Array.isArray(deptData) ? deptData : (deptData?.data || []));
            setPermissionDb(Array.isArray(permData) ? permData : (permData?.data || []));
            setDeptPermMap((Array.isArray(deptPermData) ? {} : (deptPermData?.data ?? deptPermData)) ?? {});
        } catch (e) {
            notify(e?.response?.data?.message || "부서 목록 로딩 실패", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const createDept = () => {
        const name = (newName || "").trim();
        if (!name) return notify("부서명을 입력하세요.", "warning");
        return runApiAction({
            request: () => api.post(API.departments.create, { deptName: name }),
            successMsg: "부서가 생성되었습니다.",
            onSuccess: () => { setNewName(""); load(); },
        });
    };

    const openEditDialog = (d) => { setEditTarget(d); setEditName(d.deptName); };

    const saveEdit = () => {
        const name = (editName || "").trim();
        if (!name) return notify("부서명을 입력하세요.", "warning");
        return runApiAction({
            request: () => api.put(API.departments.update(editTarget.deptId), { deptName: name }),
            successMsg: "부서명이 수정되었습니다.",
            onSuccess: () => { setEditTarget(null); setEditName(""); load(); },
        });
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Paper sx={ds.cardPadded}>
                <Typography sx={{ ...ds.typography.sectionTitle, mb: 1.5 }}>새 부서 추가</Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <TextField size="small" placeholder="부서명 입력" value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && createDept()}
                        sx={{ maxWidth: 360 }} />
                    <ActionButton variant="add"
                        startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                        onClick={createDept}
                        label="생성" />
                </Stack>
            </Paper>

            <Paper sx={ds.card}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center" }}>
                    <Typography sx={{ ...ds.typography.sectionTitle, flex: 1 }}>
                        부서 목록 <span style={{ color: ds.colors.textMuted, fontWeight: 400, fontSize: 13 }}>{departments.length}개</span>
                    </Typography>
                </Box>
                <Divider />
                {loading ? (
                    <LoadingDots />
                ) : (
                    <ResizableTable size="small" sx={ds.tableMiniSx}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 80, cursor: "pointer" }} onClick={() => toggleSort("deptId")}>{sortLabel("deptId", "ID")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("deptName")}>{sortLabel("deptName", "부서명")}</TableCell>
                                <TableCell>활성 권한</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedDepts.map(d => (
                                <TableRow key={d.deptId} hover sx={{ cursor: "pointer" }}
                                    onClick={() => openEditDialog(d)}>
                                    <TableCell sx={{ color: ds.colors.textMuted }}>{d.deptId}</TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontWeight: 600 }}>{d.deptName}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <PermChips codes={deptPermMap[d.deptId] || []} codeToLabel={codeToLabel}
                                            codeToStyle={codeToStyle}
                                            onMore={() => { setPermDeptId(d.deptId); setOpenPerm(true); }} />
                                    </TableCell>
                                </TableRow>
                            ))}
                            {sortedDepts.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 4, color: ds.colors.textMuted }}>부서가 없습니다.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </ResizableTable>
                )}
            </Paper>

            {/* ── 부서 수정 모달 (행 클릭 → 부서명 수정 / 부서권한 / 삭제) ── */}
            <FormModal
                open={Boolean(editTarget)}
                onClose={() => setEditTarget(null)}
                title={`부서 수정${editTarget ? ` — ${editTarget.deptName}` : ""}`}
                subtitle={editTarget ? `ID: ${editTarget.deptId}` : undefined}
                maxWidth="xs"
                onSave={saveEdit}
                saveLabel="수정"
                saveDisabled={!editName.trim()}
                footerExtras={editTarget && (
                    <Stack direction="row" spacing={1}>
                        <ActionButton variant="secondary"
                            startIcon={<VpnKeyIcon sx={{ fontSize: 14 }} />}
                            onClick={() => { setPermDeptId(editTarget.deptId); setOpenPerm(true); setEditTarget(null); }}
                            sx={{ fontSize: 12 }}
                            label="부서권한" />
                        <ActionButton variant="delete"
                            onClick={() => { setDeleteTarget(editTarget); setOpenDelete(true); setEditTarget(null); }}
                            sx={{ fontSize: 12 }} />
                    </Stack>
                )}
            >
                <FormField label="부서명" required>
                    <TextField autoFocus fullWidth size="small" value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") saveEdit(); }}
                        placeholder="부서명을 입력하세요" />
                </FormField>
            </FormModal>

            <DepartmentDeleteModal
                open={openDelete} onClose={() => setOpenDelete(false)}
                target={deleteTarget} departments={departments}
                onDeleted={() => { setOpenDelete(false); load(); }}
            />
            <DepartmentPermissionModal
                open={openPerm} onClose={() => setOpenPerm(false)}
                deptId={permDeptId} deptName={deptMap[permDeptId]}
                permissionDb={permissionDb}
                onSaved={() => { setOpenPerm(false); load(); }}
            />
        </Box>
    );
}
